import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { useTenant } from '@/contexts/TenantContext';
import api from '@/api';
import { NotificationBell } from './NotificationBell';
import {
  LayoutDashboard, Calendar, Key, Building2, BarChart2, Settings,
  Users, Cpu, Package, Plug, FileText, User, LogOut, Menu,
  BookOpen, Truck, Languages, Sun, Moon, Lock, Code2, Globe, HelpCircle, MessageSquare, Store, Palette, Receipt, Link2
} from 'lucide-react';
import { LangToggle, useI18n } from '@/contexts/I18nContext';

// Mappa sezione → feature richiesta (undefined = sempre disponibile)
const SECTION_FEATURE = {
  'devices':      'smart_home',
  'integrations': 'smart_home',
  'suppliers':    'supplier_portal',
  'translations': 'i18n_editor',
  'access-log':   'access_log',
  'api-keys':     'api_access',
  'webhooks':     'api_access',
  'webhook-analytics': 'api_access',
  'branding':     'white_label',
};

const NAV_ITEMS = [
  { id: 'dashboard',          tKey: 'sidebar_dashboard',      icon: LayoutDashboard },
  { id: 'booking-requests',   tKey: 'sidebar_requests',       icon: Calendar },
  { id: 'bookings',           tKey: 'sidebar_virtual_keys',   icon: Key },
  { id: 'guests',             tKey: 'sidebar_guests',         icon: Users },
  { id: 'properties',         tKey: 'sidebar_properties',     icon: Building2 },
  { id: 'rates',              tKey: 'sidebar_rates',          icon: BarChart2 },
  { id: 'services',           tKey: 'sidebar_services',       icon: BookOpen },
  { id: 'extras',             tKey: 'sidebar_extras',         icon: Package },
  { id: 'suppliers',          tKey: 'sidebar_suppliers',      icon: Truck },
  { id: 'integrations',       tKey: 'sidebar_integrations',   icon: Plug },
  { id: 'devices',            tKey: 'sidebar_devices',        icon: Cpu },
  { id: 'access-log',         tKey: 'sidebar_access_log',     icon: FileText },
  { id: 'admin-users',        tKey: 'sidebar_admin_users',    icon: User },
  { id: 'translations',       tKey: 'sidebar_translations',   icon: Languages },
  { id: 'api-keys',           tKey: 'sidebar_api_keys',       icon: Code2 },
  { id: 'webhooks',           tKey: 'sidebar_webhooks',       icon: Globe },
  { id: 'webhook-analytics',  tKey: 'sidebar_wh_analytics',   icon: BarChart2 },
  { id: 'guide',              tKey: 'sidebar_guide',          icon: HelpCircle },
  { id: 'useful-links',       tKey: 'sidebar_useful_links',   icon: Link2 },
  { id: 'message-templates',  tKey: 'sidebar_msg_templates',  icon: MessageSquare },
  { id: 'marketplace-panel',  tKey: 'sidebar_marketplace',    icon: Store },
  { id: 'branding',           tKey: 'sidebar_branding',       icon: Palette },
  { id: 'receipts',           tKey: 'sidebar_receipts',       icon: Receipt },
  { id: 'settings',           tKey: 'sidebar_settings',       icon: Settings },
];

export default function AdminLayout({ children, section, onNavigate }) {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { tenant, features, whitelabel } = useTenant();
  const { t } = useI18n();

  // White-label: sovrascrive CSS vars se configurato
  const wl = whitelabel?.enabled ? whitelabel : null;
  const primaryColor = wl?.primary_color || 'var(--lux-gold)';
  const brandName = wl?.brand_name || tenant?.name || 'Shangrilux';
  const appName = tenant?.name || 'Shangrilux';
  const [mobileOpen, setMobileOpen] = useState(false);
  const [quotesBadge, setQuotesBadge] = useState(0);

  // Helper: is this section's feature gate locked?
  const isLocked = (id) => {
    const feat = SECTION_FEATURE[id];
    if (!feat || !features) return false;
    return features[feat] === false;
  };

  useEffect(() => {
    const fetchBadge = async () => {
      try {
        const res = await api.get('/booking-requests/pending-supplier-quotes');
        setQuotesBadge(res.data.count || 0);
      } catch {
        // silently ignore (e.g. auth not ready yet)
      }
    };
    fetchBadge();
    const interval = setInterval(fetchBadge, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleNav = (id) => {
    onNavigate(id);
    setMobileOpen(false);
  };

  const Sidebar = () => (
    <div className="flex flex-col h-full">
      {/* Logo / Brand */}
      <div className="px-5 py-6 border-b border-[var(--lux-border-1)]">
        {wl?.logo_url ? (
          <img src={wl.logo_url} alt={brandName} className="h-8 object-contain mb-1"
            onError={e => { e.target.style.display = 'none'; }} />
        ) : (
          <h1 className="font-serif text-xl font-light" style={{ color: primaryColor }}>{brandName}</h1>
        )}
        {!wl?.hide_ostivex_branding && (
          <p className="label-muted text-[10px] mt-0.5">Shangrilux Luxury Hospitality</p>
        )}
        {wl?.hide_ostivex_branding && wl?.powered_by_text && (
          <p className="text-[10px] mt-0.5" style={{ color: 'var(--lux-text-muted-3)' }}>{wl.powered_by_text}</p>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 py-4 overflow-y-auto">
        {NAV_ITEMS.map(({ id, tKey, label, icon: Icon }) => {
          const active = section === id;
          const locked = isLocked(id);
          return (
            <button
              key={id}
              onClick={() => handleNav(id)}
              data-testid={`nav-${id}`}
              className={`w-full flex items-center gap-3 px-5 py-2.5 text-sm transition-colors duration-150 text-left
                ${active
                  ? 'bg-[#c9a84c]/10 text-[#c9a84c] border-r-2 border-[#c9a84c]'
                  : locked
                    ? 'text-[var(--lux-text-muted-3)] hover:text-[var(--lux-text-muted-2)] hover:bg-[var(--lux-surface-3)]'
                    : 'text-[var(--lux-text-muted-2)] hover:text-white hover:bg-[var(--lux-surface-3)]'
                }`}
            >
              <Icon size={16} strokeWidth={active ? 2 : 1.5} />
              <span className="flex-1">{t(tKey)}</span>
              {id === 'booking-requests' && quotesBadge > 0 && (
                <span
                  data-testid="quotes-badge"
                  className="min-w-[18px] h-[18px] px-1 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center leading-none"
                >
                  {quotesBadge > 9 ? '9+' : quotesBadge}
                </span>
              )}
              {locked && (
                <Lock
                  size={11}
                  data-testid={`lock-icon-${id}`}
                  style={{ color: 'rgba(201,168,76,0.45)', flexShrink: 0 }}
                />
              )}
            </button>
          );
        })}
      </nav>

      {/* User */}
      <div className="border-t border-[var(--lux-border-1)] p-4">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-7 h-7 bg-[#c9a84c]/20 rounded-full flex items-center justify-center">
            <User size={13} className="text-[#c9a84c]" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-white text-xs font-medium truncate">{user?.name}</p>
            <p className="text-[var(--lux-text-muted-3)] text-[10px] uppercase tracking-widest">{user?.role}</p>
          </div>
        </div>
        <button
          onClick={logout}
          data-testid="logout-btn"
          className="w-full flex items-center gap-2 text-[var(--lux-text-muted-3)] hover:text-red-400 text-xs transition-colors py-1"
        >
          <LogOut size={13} />
          {t('header_logout')}
        </button>
        <button onClick={toggleTheme}
          className="w-full flex items-center gap-2 text-[var(--lux-text-muted-3)] hover:text-[var(--lux-text)] text-xs transition-colors py-1 mt-0.5"
          data-testid="theme-toggle-btn"
          title={theme === 'dark' ? 'Tema chiaro' : 'Tema scuro'}>
          {theme === 'dark' ? <Sun size={13} /> : <Moon size={13} />}
          {theme === 'dark' ? 'Tema Chiaro' : 'Tema Scuro'}
        </button>
        <div className="mt-2">
          <LangToggle />
        </div>
        <p className="text-[var(--lux-border-4)] text-[9px] mt-2 pt-2 border-t border-[var(--lux-border-1)]">
          <a href="https://francescorusso.work" target="_blank" rel="noreferrer" className="hover:text-[var(--lux-text-muted)] transition-colors">
            francescorusso.work
          </a>
        </p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[var(--lux-bg)] flex">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex flex-col w-56 bg-[var(--lux-surface-1)] border-r border-[var(--lux-border-1)] sticky top-0 h-screen overflow-hidden">
        <Sidebar />
      </aside>

      {/* Mobile sidebar overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/70" onClick={() => setMobileOpen(false)} />
          <aside className="absolute left-0 top-0 bottom-0 w-56 bg-[var(--lux-surface-1)] border-r border-[var(--lux-border-1)] overflow-hidden">
            <Sidebar />
          </aside>
        </div>
      )}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar — mobile + desktop */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-[var(--lux-border-1)] bg-[var(--lux-surface-1)] sticky top-0 z-30">
          {/* Menu mobile */}
          <button onClick={() => setMobileOpen(true)} className="lg:hidden text-[var(--lux-text-muted-2)]">
            <Menu size={20} />
          </button>
          {/* Nome app (mobile: visibile; desktop: flex-1 vuoto per spingere campanellino a destra) */}
          <h1 className="font-serif text-lg text-white font-light lg:hidden">{appName}</h1>
          <div className="flex-1" />
          {/* Campanellino — sempre visibile desktop + mobile */}
          <NotificationBell />
        </div>

        {/* Content */}
        <main className="flex-1 p-6 overflow-y-auto">
          <div className="fade-in max-w-7xl mx-auto">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}
